import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle, ChevronDown, ChevronUp, ChevronsUpDown,
  Plus, Trash2, X, GripVertical, HelpCircle,
} from '../../icons';
// 채택 3단계 ② 인사 — 단추를 정본 Button 으로(D1).
import { Button, Modal, ModalActions } from '../../components/shared';
import {
  fetchJobGrades, createJobGrade, updateJobGrade, deleteJobGrade,
  adoptJobGrade, clearUnlistedPositions,
  reorderJobGrades, fetchGradeTemplateItems, addGradeTemplateItem, removeGradeTemplateItem,
  type JobGradeBoardDto, type JobGradeDto, type GradeTemplateItemDto,
} from '../../api/hr-job-grades';
import {
  GRADE_TEMPLATES, DEFAULT_TEMPLATE, templateOf, markTaken, pickToAdd,
  mergeTemplateItems, moveInOrder,
} from '../../lib/hr-grade-templates';
// 경로 한 줄만 정본 꼴로 — 제 도움말 팝업은 그대로(2026-10-17 · web 레인 W7a — PageCrumbs 머리말)
import { PageCrumbs } from '../../components/shared/PageHeader/PageCrumbs';
import { crumbsFor } from '../../lib/page-crumbs';
import './JobGradesPage.css';
// 표준 템플릿 한 줄 접힘 · 표 전체 폭 · 목록 밖 직위는 표 아래(2026-10-18 · web 레인 W10-61 · 렌즈 Toss)
import './job-grades-fold.css';
import { FoldRow } from '../../components/shared';

/**
 * 직위 · 직급 — 담당자 화면 (인사, migration 138 · 139).
 *
 * ★★ **호칭은 정식 명칭과 따로 간다**(m139) — 직책은 「수석보」인데 부를 때는 「수석」,
 *    「상무보」인데 「상무」. 비어 있으면 이름이 곧 호칭이다.
 * ★★ **순위는 끌어다 놓아 바꾼다.** 레벨 숫자를 하나씩 고치는 것보다 「누가 위인가」 를
 *    옮기는 것이 담당자가 실제로 하는 일이다. 서버는 **쓰던 레벨 값을 다시 나눠 줄 뿐**이라
 *    범위 밖으로 못 나가고 같은 급이던 둘은 같은 급으로 남는다.
 * ★★ **아래는 «현재 회사의 직책»** — 직급표에 있는 것과 없는 것을 한 목록으로 본다.
 *    예전에는 없는 것만 보여 줬는데, 그러면 「지금 우리 회사가 무엇을 쓰나」 를 볼 데가 없었다.
 * ★ 쓰는 사람이 있는 직위는 **삭제가 아니라 접기**다(지난 발령이 그 이름을 가리킨다).
 * ★ **설명 문구를 두지 않는다**(2026-08-27 사용자 지시) — 화면이 하는 일은 표와 이름으로 말한다.
 */

const emptyForm = () => ({ name: '', callName: '', level: '' });
const emptyTpl = () => ({ name: '', callName: '', level: '' });

type SortKey = 'level' | 'name' | 'people';
interface Sort { key: SortKey; dir: 'asc' | 'desc' }

const SORT_LABEL: Record<SortKey, string> = { level: '레벨', name: '직위', people: '인원' };

export function JobGradesPage() {
  const [board, setBoard] = useState<JobGradeBoardDto | null>(null);
  const [extra, setExtra] = useState<GradeTemplateItemDto[]>([]);
  const [state, setState] = useState<'loading' | 'ok' | 'folded'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [sort, setSort] = useState<Sort>({ key: 'level', dir: 'desc' });
  /**
   * ★★★ **고치기가 없었다** (2026-08-30 사용자 지적: 「호칭 수정이 안 되며」).
   *    서버에는 PATCH 가 있는데 화면이 부르지 않았다 — 행에 있는 것은 접기·삭제뿐이었다.
   *    한 줄만 열리는 인라인 폼으로 이름·호칭·등급을 고친다.
   */
  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', callName: '', level: '' });
  /** 목록 밖 직위를 담을 때 쓸 등급 — 이름마다 따로 고른다. */
  const [adoptLevel, setAdoptLevel] = useState<Record<string, string>>({});
  /**
   * ★★ 열려 있는 팝업 (2026-09-02 · 시안 확정) — 만들기·고치기는 **팝업**에서 끝난다.
   *    본문에 입력 줄을 상주시키면 매일 보는 표가 그만큼 좁아진다.
   *      grade   — 직위 등록 · 수정 (`editId` 가 있으면 수정)
   *      tpl     — 템플릿에서 고른 것 등록
   *      adopt   — 목록 밖 직위를 직급표에 등록
   *      clear   — 목록 밖 직위를 직원에게서 해제
   *      tplItem — 템플릿 자체에 항목 보태기
   */
  const [modal, setModal] = useState<null | { kind: 'grade' | 'tpl' | 'tplItem' } | { kind: 'adopt' | 'clear'; name: string }>(null);
  /** 해제 팝업이 먼저 세어 온 인원 — 「몇 명의 값이 비워지는지」를 눌리기 전에 말한다. */
  const [clearCount, setClearCount] = useState<number | null>(null);
  /** 제목 옆 도움말(정본 A3) — 설명문은 화면에 두지 않고 여기서 편다. */
  const [helpOpen, setHelpOpen] = useState(false);
  const helpBtnRef = useRef<HTMLButtonElement>(null);
  const helpRef = useRef<HTMLDivElement>(null);
  const [tplKey, setTplKey] = useState(DEFAULT_TEMPLATE.key);
  const [tplPicked, setTplPicked] = useState<Set<string>>(() => new Set());
  /** 템플릿에 항목 보태기 — 접혀 있다가 «항목 추가» 를 누르면 펼친다. */
  const [tplForm, setTplForm] = useState<ReturnType<typeof emptyTpl> | null>(null);
  /** 끌고 있는 행 · 놓일 자리. */
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  /**
   * 도움말 팝오버 — **Esc 로 닫고 트리거로 돌아온다**(정본 A3). 바깥을 눌러도 닫힌다.
   * ★ `role="dialog"` 는 안 준다 — 초점을 가두지 않는 «읽는 자리»라, 그 역할을 주면
   *   진짜 팝업(Modal)과 같은 것으로 읽히고 검사·보조기기가 둘을 구별하지 못한다.
   */
  useEffect(() => {
    if (!helpOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setHelpOpen(false);
      helpBtnRef.current?.focus();
    };
    const onDown = (e: MouseEvent) => {
      if (!(e.target instanceof Node)) return;
      if (helpRef.current?.contains(e.target) || helpBtnRef.current?.contains(e.target)) return;
      setHelpOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [helpOpen]);

  const load = useCallback(async () => {
    setBoard(await fetchJobGrades());
    // ★ 옛 서버에는 이 라우트가 없다(404) — 없으면 기본 묶음만으로 그대로 돈다.
    try { setExtra(await fetchGradeTemplateItems()); } catch { setExtra([]); }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await load();
        if (alive) setState('ok');
      } catch {
        if (alive) setState('folded');
      }
    })();
    return () => { alive = false; };
  }, [load]);

  const run = async (key: string, fn: () => Promise<void>, fail: string) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      const d = (e as { response?: { data?: { message?: string } } }).response?.data;
      setError(d?.message ?? fail);
    } finally {
      setBusy(null);
    }
  };

  const min = board?.levelRange.min ?? 1;
  const max = board?.levelRange.max ?? 20;

  const readLevel = (v: string) => {
    const n = Number(v);
    return Number.isInteger(n) && n >= min && n <= max ? n : null;
  };

  const add = () => {
    if (!form.name.trim()) { setError('직위 이름을 적어 주세요'); return; }
    const level = readLevel(form.level);
    if (level === null) { setError(`레벨은 ${min}~${max} 사이의 정수입니다`); return; }
    void run('new', async () => {
      await createJobGrade({ name: form.name.trim(), callName: form.callName.trim() || null, level });
      setForm(emptyForm());
      setModal(null);
      setNotice('등록했습니다.');
    }, '직위를 등록하지 못했습니다');
  };

  const startEdit = (g: JobGradeDto) => {
    setEditId(g.id);
    setEditForm({ name: g.name, callName: g.callName ?? '', level: String(g.level) });
    setError(null);
    setNotice(null);
    setModal({ kind: 'grade' });
  };

  /** 새로 등록 — 같은 팝업을 «빈 값»으로 연다. */
  const startNew = () => {
    setEditId(null);
    setForm(emptyForm());
    setError(null);
    setNotice(null);
    setModal({ kind: 'grade' });
  };

  const closeModal = () => { setModal(null); setEditId(null); setError(null); };

  const saveEdit = (g: JobGradeDto) => {
    if (!editForm.name.trim()) { setError('직위 이름을 적어 주세요'); return; }
    const level = readLevel(editForm.level);
    if (level === null) { setError(`레벨은 ${min}~${max} 사이의 정수입니다`); return; }
    void run(g.id, async () => {
      try {
        await updateJobGrade(g.id, {
          name: editForm.name.trim(), callName: editForm.callName.trim() || null, level,
        });
      } catch (e) {
        const d = (e as { response?: { data?: { code?: string; message?: string } } })?.response?.data;
        // ★ 등급이 겹치면 서버가 막는다. **그래도 같게 두겠는지** 한 번 더 묻고, 그때만 뚫는다 —
        //   동급 직위는 실재하지만 실수로 들어가는 것이 더 흔하다.
        if (d?.code === 'GRADE_LEVEL_TAKEN'
          && window.confirm(`${d.message}\n\n그래도 같은 등급으로 두시겠습니까?`)) {
          await updateJobGrade(g.id, {
            name: editForm.name.trim(), callName: editForm.callName.trim() || null,
            level, allowSameLevel: true,
          });
        } else {
          throw e;
        }
      }
      setEditId(null);
      setModal(null);
      setNotice('저장했습니다.');
    }, '저장하지 못했습니다');
  };

  /** 목록 밖 직위를 마스터에 담는다 — 담자마자 그 이름을 쓰는 사람의 등급이 맞춰진다. */
  const adopt = (name: string) => {
    const level = readLevel(adoptLevel[name] ?? '');
    if (level === null) { setError(`레벨은 ${min}~${max} 사이의 정수입니다`); return; }
    void run(`adopt:${name}`, async () => {
      const res = await adoptJobGrade({ name, level });
      setAdoptLevel((v) => ({ ...v, [name]: '' }));
      setModal(null);
      setNotice(`「${name}」 을 등록했습니다 — ${res.syncedUsers}명의 등급을 맞췄습니다.`);
    }, '등록하지 못했습니다');
  };

  /**
   * ⚠ 사람의 값을 지우는 일이다 — **먼저 세어 보여 주고** 확인한 뒤에만.
   * ★ 세는 것(dry-run)은 팝업을 **열 때** 하고, 확인은 그 팝업이 받는다
   *   (window.confirm 을 겹쳐 띄우면 같은 것을 두 번 묻게 된다).
   */
  const askClear = (name: string) => {
    setClearCount(null);
    setModal({ kind: 'clear', name });
    void run(`clear:${name}`, async () => {
      const dry = await clearUnlistedPositions([name], true);
      setClearCount(dry.cleared);
    }, '세지 못했습니다');
  };

  const clearUnlisted = (name: string) => {
    void run(`clear:${name}`, async () => {
      const res = await clearUnlistedPositions([name]);
      setModal(null);
      setNotice(`${res.cleared}명의 직위를 해제했습니다 — 직급표에서 다시 골라 주세요.`);
    }, '해제하지 못했습니다');
  };

  const archive = (g: JobGradeDto) =>
    run(g.id, async () => { await updateJobGrade(g.id, { active: !g.active }); }, '바꾸지 못했습니다');

  const remove = (g: JobGradeDto) =>
    run(g.id, async () => { await deleteJobGrade(g.id); }, '지우지 못했습니다');

  const removePicked = () =>
    run('bulk', async () => {
      const ids = [...picked];
      for (const id of ids) await deleteJobGrade(id);
      setPicked(new Set());
      setNotice(`${ids.length}개를 지웠습니다.`);
    }, '지우지 못했습니다');

  const addFromTemplate = () => {
    const names = board?.grades.map((g) => g.name) ?? [];
    const fresh = pickToAdd(tplRows, names, tplPicked);
    if (fresh.length === 0) { setError('담을 것이 없습니다'); return; }
    void run('tpl', async () => {
      for (const it of fresh) {
        const row = tplRows.find((r) => r.name === it.name);
        await createJobGrade({ name: it.name, callName: row?.callName ?? null, level: it.level });
      }
      setTplPicked(new Set());
      setModal(null);
      setNotice(`${fresh.length}개를 등록했습니다.`);
    }, '등록하지 못했습니다');
  };

  /** 템플릿에 항목 보태기 — 다음에 또 치지 않게 목록에 남는다. */
  const addTemplateItem = () => {
    if (!tplForm) return;
    if (!tplForm.name.trim()) { setError('이름을 적어 주세요'); return; }
    const level = readLevel(tplForm.level);
    if (level === null) { setError(`레벨은 ${min}~${max} 사이의 정수입니다`); return; }
    void run('tplAdd', async () => {
      await addGradeTemplateItem({
        templateKey: tplKey, name: tplForm.name.trim(),
        callName: tplForm.callName.trim() || null, level,
      });
      setTplForm(null);
      setModal(null);
      setNotice('템플릿에 보탰습니다.');
    }, '보태지 못했습니다');
  };

  const dropTemplateItem = (id: string) =>
    run(`tpl:${id}`, async () => { await removeGradeTemplateItem(id); }, '지우지 못했습니다');

  const toggle = (set: Set<string>, key: string) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  };

  const sortBy = (key: SortKey) =>
    setSort((s) => (s.key === key
      ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' }
      : { key, dir: key === 'name' ? 'asc' : 'desc' }));

  /** ★ 접힌 직위는 늘 뒤로 — 정렬을 바꿔도 그대로다. */
  const rows = useMemo(() => {
    const list = [...(board?.grades ?? [])];
    const dir = sort.dir === 'asc' ? 1 : -1;
    const byName = (a: JobGradeDto, b: JobGradeDto) => a.name.localeCompare(b.name, 'ko');
    list.sort((a, b) => {
      if (a.active !== b.active) return a.active ? -1 : 1;
      if (sort.key === 'name') return byName(a, b) * dir;
      if (sort.key === 'people') return (a.people - b.people) * dir || byName(a, b);
      return (a.level - b.level) * dir || byName(a, b);
    });
    return list;
  }, [board, sort]);

  /**
   * ★ **순위 바꾸기는 «레벨 높은 순» 일 때만 된다.** 이름순으로 보고 있는데 끌어 옮기면
   *   무엇이 무엇 위로 가는지 화면과 뜻이 어긋난다.
   */
  const canDrag = sort.key === 'level' && sort.dir === 'desc' && busy === null;
  const active = rows.filter((g) => g.active);

  const drop = (targetId: string) => {
    setOverId(null);
    const from = active.findIndex((g) => g.id === dragId);
    const to = active.findIndex((g) => g.id === targetId);
    setDragId(null);
    if (from < 0 || to < 0 || from === to) return;
    const ids = moveInOrder(active, from, to).map((g) => g.id);
    void run('order', async () => {
      const r = await reorderJobGrades(ids);
      setNotice(r.changed > 0 ? '순위를 바꿨습니다.' : null);
    }, '순위를 바꾸지 못했습니다');
  };

  const tpl = templateOf(tplKey);
  const tplRows = mergeTemplateItems(tpl.items, extra.filter((e) => e.templateKey === tplKey));
  const tplMarked = markTaken(tplRows, board?.grades.map((g) => g.name) ?? []);
  const tplFresh = tplMarked.filter((r) => !r.taken && tplPicked.has(r.name)).length;
  // 접힌 줄의 수(W10-61) — 고른 묶음의 항목 수 · 그중 아직 없는(새로 가져올 수 있는) 수. «있음» 판정은 위 markTaken 그대로.
  const tplLabel = GRADE_TEMPLATES.find((t) => t.key === tplKey)?.label ?? tplKey;
  const tplNew = tplMarked.filter((r) => !r.taken).length;

  const s = board?.summary;
  const sortIcon = (key: SortKey) =>
    sort.key !== key
      ? <ChevronsUpDown size={13} className="hrjg__sorti" aria-hidden />
      : sort.dir === 'desc'
        ? <ChevronDown size={13} aria-hidden />
        : <ChevronUp size={13} aria-hidden />;
  const ariaSort = (key: SortKey): 'ascending' | 'descending' | 'none' =>
    (sort.key === key ? (sort.dir === 'desc' ? 'descending' : 'ascending') : 'none');

  const editing = editId ? rows.find((g) => g.id === editId) ?? null : null;
  const fields = editing ? editForm : form;
  const setFields = (v: { name: string; callName: string; level: string }) =>
    (editing ? setEditForm(v) : setForm(v));

  return (
    <div className="hrpage hrjg">
      {/* ── 화면 머리 — 제목 · 도움말 · 요약 · 대표 행동 (정본 P3-1) ─────────
          ★ 설명 문구를 여기 두지 않는다. 「원래 그런 규칙」은 도움말이 맡는다(정본 P8). */}
      <header className="hrpage__head hrjg__head">
        <div className="hrjg__hgroup">
          <PageCrumbs items={crumbsFor('/erp/hr/job-grades')} />
          <div className="hrjg__hd">
            <h1>직위 · 직급</h1>
          <Button
            ref={helpBtnRef}
            variant="ghost"
            size="compact"
            iconOnly
            className="hrjg__helpbtn"
            aria-label="직위 · 직급 도움말"
            aria-expanded={helpOpen}
            onClick={() => setHelpOpen((v) => !v)}
            iconLeft={<HelpCircle size={16} aria-hidden />}
          />
          {helpOpen && (
            <div className="hrjg__help" role="note" aria-label="직위 · 직급 도움말" ref={helpRef}>
              <div className="hrjg__helph">
                <HelpCircle size={15} aria-hidden />
                <b>직위 · 직급</b>
                <span className="hrjg__sp" />
                <Button
                  variant="ghost" size="sm" iconOnly aria-label="닫기"
                  onClick={() => setHelpOpen(false)} iconLeft={<X size={13} aria-hidden />}
                />
              </div>
              <div className="hrjg__helpb">
                · <b>호칭</b>은 정식 명칭과 따로 갑니다 — 「상무보」인데 부를 때는 「상무」.<br />
                · <b>레벨</b>은 클수록 높습니다. 겹쳐도 되며 <b>같은 급</b>으로 표시됩니다.<br />
                · 쓰는 사람이 있으면 <b>삭제할 수 없습니다.</b> 새로 쓰지 않으려면 <b>중지</b>.<br />
                · <b>목록 밖 직위</b>는 직원에게만 있는 이름입니다 — 등록하거나 해제합니다.
              </div>
            </div>
            )}
          </div>
        </div>
        {s && (
          <span className="hrjg__kpis">
            <span className="hrjg__kpi">직위 <b>{s.active}</b></span>
            <span className="hrjg__kpi">직위 없음 <b>{s.noPosition}</b></span>
          </span>
        )}
        <Button
          variant="primary"
          size="compact"
          onClick={startNew}
          iconLeft={<Plus size={14} aria-hidden />}
        >
          직위 등록
        </Button>
      </header>

      {state === 'folded' && (
        <div className="hrpage__note">직위 · 직급을 불러오지 못했습니다.</div>
      )}

      {state !== 'folded' && (
        <>
          {/* ★ 팝업이 열려 있으면 오류는 **그 안에서만** 말한다 — 두 곳에 같은 문장이 서면
                 읽어 주는 쪽은 같은 오류를 두 번 듣는다(검사도 «alert 둘» 로 걸렸다). */}
          {error && !modal && <p className="hrcert__err" role="alert">{error}</p>}
          {notice && <p className="hrjg__notice" role="status">{notice}</p>}

          {board && board.duplicates.length > 0 && (
            <div className="hrasg__overdue" role="status">
              <AlertCircle size={16} aria-hidden />
              같은 급 — {board.duplicates.map((d) => `레벨 ${d.level}: ${d.names.join(' · ')}`).join(' / ')}
            </div>
          )}

          {/* ── 두 칸 — 왼쪽은 «담을 것», 오른쪽은 «담긴 것» (정본 P3-1) ── */}
          <div className="hrjg__two">
            <div className="hrjg__side">
              {/* W10-61(렌즈 Toss) — 늘 펼친 왼쪽 패널 → 표 위 한 줄 접힘. 펼치면 종전 패널이 그대로 나온다. */}
              <FoldRow
                className="hrjg__fold"
                summary={<>기본 목록 «{tplLabel}» {tplMarked.length}개 중 <b>{tplNew}개</b>를 새로 가져올 수 있음</>}
              >
              <section className="hrjg__panel" aria-label="표준 템플릿">
                <div className="hrjg__panelh">
                  <span className="hrjg__panelt">표준 템플릿</span>
                  <span className="hrjg__paneln">골라서 등록한다</span>
                </div>
                <div className="hrjg__panelb">
                  <label className="hrjg__tplpick">
                    <span className="hrjg__sr">묶음</span>
                    <select
                      aria-label="템플릿 묶음"
                      value={tplKey}
                      onChange={(e) => { setTplKey(e.target.value); setTplPicked(new Set()); setTplForm(null); }}
                    >
                      {GRADE_TEMPLATES.map((t) => (
                        <option key={t.key} value={t.key}>{t.label}</option>
                      ))}
                    </select>
                  </label>

                  <ul className="hrjg__tpllist">
                    {tplMarked.map((it) => {
                      const row = tplRows.find((r) => r.name === it.name);
                      const on = tplPicked.has(it.name);
                      return (
                        <li key={it.name} className={it.taken ? 'is-taken' : (on ? 'is-on' : undefined)}>
                          <label className="hrjg__tplrow">
                            <input
                              type="checkbox" disabled={it.taken} checked={on}
                              aria-label={`${it.name} 고르기`}
                              onChange={() => setTplPicked((p) => toggle(p, it.name))}
                            />
                            <span className="hrjg__tplnm">{it.name}</span>
                            {row?.callName && <span className="hrjg__tplcall">{row.callName}</span>}
                            {it.taken && <span className="hrjg__have">있음</span>}
                            <span className="hrjg__tpllv">{it.level}</span>
                          </label>
                          {row?.itemId && (
                            <Button
                              variant="ghost" size="sm" iconOnly
                              aria-label={`${it.name} 템플릿에서 지우기`}
                              disabled={busy === `tpl:${row.itemId}`}
                              onClick={() => dropTemplateItem(row.itemId!)}
                              iconLeft={<X size={13} aria-hidden />}
                            />
                          )}
                        </li>
                      );
                    })}
                  </ul>

                  <div className="hrjg__tplfoot">
                    <Button
                      variant="secondary" size="compact"
                      disabled={tplFresh === 0 || busy === 'tpl'}
                      onClick={() => { setError(null); setModal({ kind: 'tpl' }); }}
                    >
                      {tplFresh > 0 ? `${tplFresh}개 등록` : '등록'}
                    </Button>
                    <Button
                      variant="ghost" size="compact"
                      onClick={() => { setTplForm(emptyTpl()); setError(null); setModal({ kind: 'tplItem' }); }}
                      iconLeft={<Plus size={14} aria-hidden />}
                    >
                      항목 추가
                    </Button>
                  </div>
                </div>
              </section>
              </FoldRow>

            </div>

            {/* ── 오른쪽: 직급표 ── */}
            <section className="hrjg__panel hrjg__main" aria-label="직급표">
              <div className="hrjg__panelh">
                <span className="hrjg__panelt">직급표</span>
                <span className="hrjg__paneln">끌어서 순위를 바꾼다</span>
                <span className="hrjg__sp" />
                <Button
                  variant="quiet-danger"
                  size="compact"
                  aria-label={picked.size > 0 ? `고른 ${picked.size}개 삭제` : '고른 것 삭제'}
                  onClick={removePicked}
                  disabled={picked.size === 0 || busy === 'bulk'}
                  iconLeft={<Trash2 size={14} aria-hidden />}
                  iconRight={picked.size > 0 ? <span className="hrjg__cnt" aria-hidden>{picked.size}</span> : undefined}
                >
                  선택 삭제
                </Button>
              </div>

              {!board || board.grades.length === 0 ? (
                <p className="hrpage__empty">왼쪽 <b>표준 템플릿</b>에서 골라 등록하면 시작됩니다.</p>
              ) : (
                <div className="hrjg__tblwrap">
                  <table className="hrpage__tbl hrjg__tbl">
                    <thead>
                      <tr>
                        <th className="hrjg__grabcell" aria-hidden />
                        <th className="hrjg__pickcell" aria-hidden />
                        <th className="hrpage__num" aria-sort={ariaSort('level')}>
                          <button type="button" className="hrjg__sort" onClick={() => sortBy('level')}>
                            {SORT_LABEL.level}{sortIcon('level')}
                          </button>
                        </th>
                        <th aria-hidden />
                        <th aria-sort={ariaSort('name')}>
                          <button type="button" className="hrjg__sort" onClick={() => sortBy('name')}>
                            {SORT_LABEL.name}{sortIcon('name')}
                          </button>
                        </th>
                        <th>호칭</th>
                        <th className="hrpage__num" aria-sort={ariaSort('people')}>
                          <button type="button" className="hrjg__sort" onClick={() => sortBy('people')}>
                            {SORT_LABEL.people}{sortIcon('people')}
                          </button>
                        </th>
                        <th>상태</th>
                        <th className="hrpage__act">&nbsp;</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((g) => {
                        const grab = canDrag && g.active;
                        return (
                          <tr
                            key={g.id}
                            className={[
                              g.active ? '' : 'hrjg__off',
                              dragId === g.id ? 'hrjg__dragging' : '',
                              overId === g.id ? 'hrjg__over' : '',
                            ].filter(Boolean).join(' ')}
                            draggable={grab}
                            onDragStart={grab ? () => setDragId(g.id) : undefined}
                            // ★ preventDefault 가 없으면 drop 이 아예 안 온다.
                            onDragOver={grab ? (e) => { e.preventDefault(); setOverId(g.id); } : undefined}
                            onDrop={grab ? (e) => { e.preventDefault(); drop(g.id); } : undefined}
                            onDragEnd={() => { setDragId(null); setOverId(null); }}
                          >
                            <td className="hrjg__grabcell">
                              <span
                                className={`hrjg__grab${grab ? '' : ' hrjg__grab--off'}`}
                                aria-hidden
                                title={grab
                                  ? '끌어서 순위 바꾸기'
                                  : g.active ? '레벨 높은 순일 때 순위를 바꿀 수 있습니다' : undefined}
                              >
                                <GripVertical size={15} />
                              </span>
                            </td>
                            <td className="hrjg__pickcell">
                              <input
                                type="checkbox" checked={picked.has(g.id)} disabled={g.people > 0}
                                aria-label={`${g.name} 고르기`}
                                onChange={() => setPicked((p) => toggle(p, g.id))}
                              />
                            </td>
                            <td className="hrpage__num hrjg__level">{g.level}</td>
                            <td className="hrjg__barcell">
                              <span className="hrjg__bar" aria-hidden>
                                <i style={{ width: `${Math.round((g.level / max) * 100)}%` }} />
                              </span>
                            </td>
                            <td className="hrjg__name">{g.name}</td>
                            <td className="hrjg__call">{g.callName ?? '—'}</td>
                            <td className="hrpage__num hrpage__dim">{g.people}명</td>
                            <td>
                              <span className={`hrpage__pill hrjg__st${g.active ? ' is-on' : ' is-off'}`}>
                                {g.active ? '사용' : '중지'}
                              </span>
                            </td>
                            <td className="hrpage__act">
                              <Button
                                variant="secondary" size="sm"
                                onClick={() => startEdit(g)} disabled={busy === g.id}
                              >
                                수정
                              </Button>
                              <Button
                                variant="secondary" size="sm"
                                onClick={() => archive(g)} disabled={busy === g.id}
                              >
                                {g.active ? '중지' : '사용'}
                              </Button>
                              <Button
                                variant="quiet-danger" size="sm"
                                onClick={() => remove(g)} disabled={busy === g.id || g.people > 0}
                                title={g.people > 0
                                  ? `${g.people}명이 쓰는 중이라 지울 수 없습니다 — 새로 쓰지 않으려면 중지`
                                  : undefined}
                              >
                                삭제
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* W10-61(관제 결정) — 목록 밖 직위는 접지 않고 표 아래 보이는 자리. 덩이는 종전 그대로 옮겼다. */}
              {board && board.unlisted.length > 0 && (
                <section className="hrjg__panel" aria-label="목록 밖 직위">
                  <div className="hrjg__panelh">
                    <span className="hrjg__panelt">목록 밖 직위</span>
                    <span className="hrjg__have">{board.unlisted.length}</span>
                    <span className="hrjg__paneln">직원에게만 있다</span>
                  </div>
                  <ul className="hrjg__unllist">
                    {board.unlisted.map((u) => (
                      <li key={u.name}>
                        <b>{u.name}</b>
                        <span className="hrpage__dim">{u.count}명</span>
                        <span className="hrjg__sp" />
                        <Button
                          variant="secondary" size="sm"
                          disabled={busy === `adopt:${u.name}`}
                          onClick={() => { setError(null); setModal({ kind: 'adopt', name: u.name }); }}
                        >
                          등록
                        </Button>
                        <Button
                          variant="quiet-danger" size="sm"
                          disabled={busy === `clear:${u.name}`}
                          onClick={() => askClear(u.name)}
                        >
                          해제
                        </Button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
          </div>
        </>
      )}

      {/* ══════════ 팝업 — 만들고 고치는 일은 여기서 끝난다 ══════════ */}
      {modal?.kind === 'grade' && (
        <Modal
          title={editing ? `${editing.name} 수정` : '직위 등록'}
          size="sm"
          onClose={closeModal}
          footer={(
            <>
              {editing && (
                <Button
                  variant="quiet-danger"
                  size="compact"
                  disabled={editing.people > 0 || busy === editing.id}
                  title={editing.people > 0
                    ? `${editing.people}명이 쓰는 중이라 지울 수 없습니다 — 새로 쓰지 않으려면 중지`
                    : undefined}
                  onClick={() => { remove(editing); closeModal(); }}
                  iconLeft={<Trash2 size={14} aria-hidden />}
                >
                  삭제
                </Button>
              )}
              <ModalActions>
                <Button variant="secondary" onClick={closeModal}>취소</Button>
                <Button
                  variant="primary"
                  disabled={busy === 'new' || (editing ? busy === editing.id : false)}
                  onClick={() => (editing ? saveEdit(editing) : add())}
                >
                  저장
                </Button>
              </ModalActions>
            </>
          )}
        >
          {error && <p className="hrcert__err" role="alert">{error}</p>}
          <div className="hrjg__form">
            <label className="hrcert__field">
              <span>직위</span>
              <input
                autoFocus value={fields.name} maxLength={50} placeholder="예) 수석보"
                aria-label={editing ? `${editing.name} 이름 고치기` : '직위'}
                onChange={(e) => setFields({ ...fields, name: e.target.value })}
              />
            </label>
            <label className="hrcert__field">
              <span>호칭</span>
              <input
                value={fields.callName} maxLength={50} placeholder="비우면 직위가 곧 호칭"
                aria-label={editing ? `${editing.name} 호칭 고치기` : '호칭'}
                onChange={(e) => setFields({ ...fields, callName: e.target.value })}
              />
            </label>
            <label className="hrcert__field hrjg__lvfield">
              <span>레벨</span>
              <input
                type="number" value={fields.level} min={min} max={max}
                aria-label={editing ? `${editing.name} 등급` : '레벨'}
                onChange={(e) => setFields({ ...fields, level: e.target.value })}
              />
            </label>
          </div>
          {editing && editing.people > 0 && (
            <p className="hrjg__mnote">
              {editing.people}명이 쓰는 중이라 <b>삭제할 수 없습니다.</b> 새로 쓰지 않으려면 표에서 <b>중지</b>로 두세요.
            </p>
          )}
        </Modal>
      )}

      {modal?.kind === 'tpl' && (
        <Modal
          title="직급표에 등록"
          size="sm"
          onClose={closeModal}
          footer={(
            <ModalActions>
              <Button variant="secondary" onClick={closeModal}>취소</Button>
              <Button variant="primary" onClick={addFromTemplate} disabled={busy === 'tpl'}>
                {tplFresh > 0 ? `${tplFresh}개 등록` : '등록'}
              </Button>
            </ModalActions>
          )}
        >
          {error && <p className="hrcert__err" role="alert">{error}</p>}
          <ul className="hrjg__addlist">
            {tplMarked.filter((it) => !it.taken && tplPicked.has(it.name)).map((it) => {
              const row = tplRows.find((r) => r.name === it.name);
              return (
                <li key={it.name}>
                  <b>{it.name}</b>
                  {row?.callName && <span className="hrjg__tplcall">{row.callName}</span>}
                  <span className="hrjg__sp" />
                  <span className="hrpage__dim">레벨 {it.level}</span>
                </li>
              );
            })}
          </ul>
          <p className="hrjg__mnote">레벨은 <b>제안값</b>입니다 — 등록한 뒤 표에서 고칠 수 있습니다.</p>
        </Modal>
      )}

      {modal?.kind === 'tplItem' && tplForm && (
        <Modal
          title="템플릿에 항목 보태기"
          size="sm"
          onClose={() => { setTplForm(null); closeModal(); }}
          footer={(
            <ModalActions>
              <Button variant="secondary" onClick={() => { setTplForm(null); closeModal(); }}>취소</Button>
              <Button variant="primary" onClick={addTemplateItem} disabled={busy === 'tplAdd'}>보태기</Button>
            </ModalActions>
          )}
        >
          {error && <p className="hrcert__err" role="alert">{error}</p>}
          <div className="hrjg__form">
            <label className="hrcert__field">
              <span>명칭</span>
              <input
                autoFocus value={tplForm.name} maxLength={50} placeholder="예) 수석보"
                onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })}
              />
            </label>
            <label className="hrcert__field">
              <span>호칭</span>
              <input
                value={tplForm.callName} maxLength={50} placeholder="예) 수석"
                onChange={(e) => setTplForm({ ...tplForm, callName: e.target.value })}
              />
            </label>
            <label className="hrcert__field hrjg__lvfield">
              <span>레벨</span>
              <input
                type="number" value={tplForm.level} min={min} max={max}
                onChange={(e) => setTplForm({ ...tplForm, level: e.target.value })}
              />
            </label>
          </div>
          <p className="hrjg__mnote">보탠 항목은 <b>다음에도 목록에 남습니다</b> — 또 치지 않아도 됩니다.</p>
        </Modal>
      )}

      {modal?.kind === 'adopt' && (
        <Modal
          title="등록"
          size="sm"
          onClose={closeModal}
          footer={(
            <ModalActions>
              <Button variant="secondary" onClick={closeModal}>취소</Button>
              <Button
                variant="primary"
                onClick={() => adopt(modal.name)}
                disabled={busy === `adopt:${modal.name}`}
              >
                등록
              </Button>
            </ModalActions>
          )}
        >
          {error && <p className="hrcert__err" role="alert">{error}</p>}
          <div className="hrjg__form">
            <label className="hrcert__field">
              <span>직위</span>
              <input value={modal.name} readOnly aria-label="직위" />
            </label>
            <label className="hrcert__field hrjg__lvfield">
              <span>레벨</span>
              <input
                autoFocus inputMode="numeric" placeholder={`${min}~${max}`}
                aria-label={`${modal.name} 등급`}
                value={adoptLevel[modal.name] ?? ''}
                onChange={(e) => setAdoptLevel((v) => ({ ...v, [modal.name]: e.target.value }))}
              />
            </label>
          </div>
          <p className="hrjg__mnote">
            직급표에 넣고, 이 이름을 쓰는 <b>직원의 등급도 함께</b> 맞춥니다.
          </p>
        </Modal>
      )}

      {modal?.kind === 'clear' && (
        <Modal
          title="해제"
          size="sm"
          onClose={closeModal}
          footer={(
            <ModalActions>
              <Button variant="secondary" onClick={closeModal}>취소</Button>
              <Button
                variant="danger"
                onClick={() => clearUnlisted(modal.name)}
                disabled={busy === `clear:${modal.name}` || clearCount === 0}
              >
                해제
              </Button>
            </ModalActions>
          )}
        >
          {error && <p className="hrcert__err" role="alert">{error}</p>}
          <p className="hrjg__mlead">
            <b>{modal.name}</b>을(를) 쓰는{' '}
            {clearCount === null ? '직원' : <b>{clearCount}명</b>}의 <b>직위가 비워집니다.</b>
          </p>
          <p className="hrjg__mnote">
            직급표에는 아무 일도 일어나지 않습니다. 비운 뒤에는 직원 관리에서
            <b> 직급표의 이름 중 하나로</b> 다시 고르게 됩니다. <b>되돌릴 수 없습니다.</b>
          </p>
        </Modal>
      )}
    </div>
  );
}
